import React, { useEffect, useId, useRef } from "react";
import { X } from "lucide-react";
import { FOCUSABLE_SELECTOR, isTopDialog, pushDialog, removeDialog, trapTabKey } from "./dialogStack";
import { useAnimatedClose } from "../../hooks/useAnimatedClose";

const EXIT_DURATION_MS = 180;

/**
 * `initialFocusRef` (optional) names the element to focus on open, e.g. a
 * form's first field. Without it the first focusable element (the close
 * button) gets focus.
 */
function Modal({ title, onClose, children, footer, initialFocusRef }) {
  const dialogRef = useRef(null);
  const lastFocusedRef = useRef(null);
  const titleId = useId();

  // Closing from inside the dialog (close button, backdrop, Escape) plays the
  // exit animation first. useAnimatedClose reads onClose through a ref, so a
  // new onClose identity never re-runs the effect below and bounces focus.
  const { closing, requestClose } = useAnimatedClose(onClose, EXIT_DURATION_MS);

  useEffect(() => {
    lastFocusedRef.current = document.activeElement;
    const dialog = dialogRef.current;
    const token = pushDialog();
    // Deferred a tick: focusing synchronously inside this effect (which
    // itself commits inside the click handler that opened the modal)
    // triggers a spurious "component suspended inside an act scope"
    // warning under React 19 + jsdom. A micro-delay avoids it and is
    // imperceptible to the user.
    const focusTimer = setTimeout(() => {
      const target = initialFocusRef?.current || dialog?.querySelector(FOCUSABLE_SELECTOR);
      target?.focus();
    }, 0);

    // Bubble phase (not capture) so a focused control inside the dialog can
    // handle Escape itself and stopPropagation (e.g. an inline rename field).
    const handleKeyDown = (event) => {
      if (!isTopDialog(token)) return;

      if (event.key === "Escape") {
        requestClose();
        return;
      }

      if (event.key === "Tab") trapTabKey(event, dialog);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      clearTimeout(focusTimer);
      removeDialog(token);
      document.removeEventListener("keydown", handleKeyDown);
      if (lastFocusedRef.current instanceof HTMLElement) {
        lastFocusedRef.current.focus();
      }
    };
    // initialFocusRef is a ref object (only its value at open time matters),
    // and requestClose is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`overlay${closing ? " is-closing" : ""}`} onMouseDown={requestClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id={titleId}>{title}</h2>
          <button type="button" className="icon-btn" aria-label="Close dialog" onClick={requestClose}>
            <X size={18} strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

export default Modal;
